<template>
  <div class="container py-4 home">
    <div class="row">
      <div class="col-md-12">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h2 class="mb-0">Upcoming Events</h2>
          <small class="text-muted">Showing current and future events only</small>
        </div>

        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else-if="upcomingEvents.length === 0" class="alert alert-info">
          <h5>No upcoming events found</h5>
          <p>There are no current or future events scheduled at this time.</p>
        </div>

        <div v-else class="row">
          <div class="col-md-12">
            <!-- Events List -->
            <div class="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
              <div
                class="col"
                v-for="event in upcomingEvents"
                :key="event.id"
              >
                <div class="card h-100 event-card">
                  <div class="card-body">
                    <h5 class="card-title">{{ event.name }}</h5>
                    <h6 class="card-subtitle mb-2 text-muted">{{ event.teamName }}</h6>

                    <div class="event-details">
                      <div class="mb-2">
                        <i class="fas fa-calendar me-2"></i>
                        <strong>{{ formatDate(event.date) }}</strong>
                      </div>

                      <div class="mb-2" v-if="event.time">
                        <i class="fas fa-clock me-2"></i>
                        {{ event.time }}
                      </div>

                      <div class="mb-2">
                        <i class="fas fa-map-marker-alt me-2"></i>
                        {{ event.location }}
                      </div>

                      <div class="mb-2" v-if="event.clubName">
                        <i class="fas fa-shield-alt me-2"></i>
                        {{ event.clubName }}
                      </div>
                    </div>

                    <div v-if="event.description" class="mt-3">
                      <p class="card-text">
                        {{ event.description.length > 100 ? event.description.substring(0, 100) + '...' : event.description }}
                      </p>
                    </div>
                  </div>

                  <div class="card-footer bg-transparent">
                    <small class="text-muted">
                      <i class="fas fa-calendar-plus me-1"></i>
                      {{ getDaysUntilEvent(event.date) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { db } from "../firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { useToast } from "vue-toastification";

export default {
  name: "MyEvents",
  setup() {
    const events = ref([]);
    const loading = ref(true);
    const toast = useToast();

    const upcomingEvents = computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      return events.value
        .filter(event => {
          if (!event.date) return false;
          const eventDate = new Date(event.date);
          return eventDate >= today;
        })
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB; // Sort by date ascending (earliest first)
        });
    });

    const fetchEvents = async () => {
      try {
        loading.value = true;
        const querySnapshot = await getDocs(collection(db, "teamEvents"));

        const eventPromises = querySnapshot.docs.map(async (docSnapshot) => {
          const eventData = docSnapshot.data();
          let teamName = "Unknown Team";
          let clubName = "";

          // Fetch team name and club info
          if (eventData.teamId) {
            try {
              const teamDoc = await getDoc(doc(db, "teams", eventData.teamId));
              if (teamDoc.exists()) {
                const teamData = teamDoc.data();
                teamName = teamData.name;

                // Fetch club name if team has a club
                if (teamData.club) {
                  const clubDoc = await getDoc(doc(db, "clubs", teamData.club));
                  if (clubDoc.exists()) {
                    clubName = clubDoc.data().name;
                  }
                }
              }
            } catch (error) {
              console.error("Error fetching team/club:", error);
            }
          }

          return {
            id: docSnapshot.id,
            ...eventData,
            teamName,
            clubName
          };
        });

        events.value = await Promise.all(eventPromises);
      } catch (error) {
        console.error("Error fetching events:", error);
        toast.error("Error fetching events");
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return dateString;
      }
    };

    const getDaysUntilEvent = (dateString) => {
      if (!dateString) return "Date TBD";

      try {
        const eventDate = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        eventDate.setHours(0, 0, 0, 0);

        const diffTime = eventDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
          return "Today";
        } else if (diffDays === 1) {
          return "Tomorrow";
        } else if (diffDays > 1) {
          return `In ${diffDays} days`;
        } else {
          return "Past event";
        }
      } catch (error) {
        return "Date TBD";
      }
    };

    onMounted(() => {
      fetchEvents();
    });

    return {
      events,
      upcomingEvents,
      loading,
      formatDate,
      getDaysUntilEvent
    };
  }
};
</script>

<style scoped>
.event-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border-left: 4px solid #007bff;
}

.event-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.event-details i {
  color: #6c757d;
  width: 16px;
}

.card-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.125);
}
</style>
